import React from "react";
import {
  YStack,
  XStack,
  SizableText,
  H3,
  H4,
  Card,
  Separator,
  Button,
  Spinner
} from "tamagui";
import { useQuery } from "@tanstack/react-query";
import { MaterialCommunityIcons, MaterialIcons } from "@expo/vector-icons";
import * as Animatable from 'react-native-animatable';
import { View, Linking, Dimensions } from "react-native";
import ErrorBoundary from "@utils/ErrorBoundary";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import apis from "@api/api";

const FacebookOptions = () => {
  const theme = themeSelector();
  const screenWidth = Dimensions.get('window').width;
  const isTablet = screenWidth > 768;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["facebookProfile"],
    queryFn: async () => {
      if (typeof apis.getFacebookProfile === 'function') {
        return await apis.getFacebookProfile();
      }
      return {
        id: "mock_id",
        name: "Facebook User",
        email: "user@example.com"
      };
    },
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });

  return (
    <ErrorBoundary>
      <YStack gap="$4">
        {/* Error State */}
        {error && (
          <Animatable.View animation="fadeInUp" duration={600}>
            <Card
              backgroundColor={theme ? "#fef2f2" : "#450a0a"}
              borderColor={theme ? "#fecaca" : "#7f1d1d"}
              borderRadius={16}
              padding="$5"
              elevate
            >
              <YStack alignItems="center" gap="$4">
                <View style={{
                  backgroundColor: theme ? "#fee2e2" : "#7f1d1d",
                  borderRadius: 50,
                  padding: 16
                }}>
                  <MaterialIcons name="error-outline" size={48} color="#ef4444" />
                </View>
                <H3 color="#ef4444" fontWeight="600" textAlign="center">
                  Failed to load Facebook profile
                </H3>
                <SizableText
                  size="$4"
                  color={theme ? "#64748b" : "#94a3b8"}
                  textAlign="center"
                >
                  Please try reconnecting your Facebook account, or try removing and re-adding the integration.
                </SizableText>
                <Button
                  backgroundColor="#ef4444"
                  color="white"
                  onPress={() => refetch()}
                  borderRadius={12}
                  paddingHorizontal="$5"
                  paddingVertical="$3"
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialIcons name="refresh" size={20} color="white" />
                    <SizableText color="white" fontWeight="600">
                      Retry Connection
                    </SizableText>
                  </XStack>
                </Button>
              </YStack>
            </Card>
          </Animatable.View>
        )}

        {/* Loading State */}
        {isLoading && (
          <Animatable.View animation="fadeIn" duration={600}>
            <Card
              backgroundColor={theme ? "#f8fafc" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
              borderRadius={16}
              padding="$6"
              elevate
            >
              <YStack alignItems="center" gap="$4">
                <Spinner size="large" color="#1877F2" />
                <H4 color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">
                  Loading Facebook profile...
                </H4>
              </YStack>
            </Card>
          </Animatable.View>
        )}

        {/* Profile Data */}
        {data?.id && (
          <>
            <Animatable.View animation="fadeInUp" duration={600} delay={100}>
              <Card
                backgroundColor={theme ? "#ffffff" : "#1e293b"}
                borderColor={theme ? "#e2e8f0" : "#334155"}
                borderRadius={16}
                padding="$5"
                elevate
              >
                {/* Header with Profile Info */}
                <XStack alignItems="center" gap="$4" marginBottom="$4">
                  <View style={{
                    width: 70,
                    height: 70,
                    borderRadius: 35,
                    backgroundColor: '#1877F2',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    <SizableText color="white" fontSize="$9" fontWeight="bold">
                      {data.name ? data.name.charAt(0).toUpperCase() : 'FB'}
                    </SizableText>
                  </View>

                  <YStack flex={1}>
                    <H4
                      color={theme ? "#1e293b" : "#f1f5f9"}
                      fontWeight="700"
                      numberOfLines={1}
                    >
                      {data.name || 'Facebook User'}
                    </H4>
                    <SizableText
                      size="$4"
                      color={theme ? "#64748b" : "#94a3b8"}
                      numberOfLines={1}
                    >
                      {data.email || `ID: ${data.id}`}
                    </SizableText>
                  </YStack>
                </XStack>

                <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginBottom="$4" />

                {/* Profile Details */}
                <YStack gap="$3">
                  <XStack alignItems="center" gap="$3">
                    <View style={{
                      backgroundColor: theme ? "#dbeafe" : "#1e3a8a",
                      borderRadius: 8,
                      padding: 8
                    }}>
                      <MaterialIcons name="person" size={20} color="#1877F2" />
                    </View>
                    <YStack flex={1}>
                      <SizableText
                        size="$2"
                        color={theme ? "#64748b" : "#94a3b8"}
                      >
                        User ID
                      </SizableText>
                      <SizableText
                        size="$4"
                        color={theme ? "#1e293b" : "#f1f5f9"}
                        fontWeight="600"
                      >
                        {data.id}
                      </SizableText>
                    </YStack>
                  </XStack>

                  {data.email && (
                    <XStack alignItems="center" gap="$3">
                      <View style={{
                        backgroundColor: theme ? "#dbeafe" : "#1e3a8a",
                        borderRadius: 8,
                        padding: 8
                      }}>
                        <MaterialIcons name="email" size={20} color="#1877F2" />
                      </View>
                      <YStack flex={1}>
                        <SizableText
                          size="$2"
                          color={theme ? "#64748b" : "#94a3b8"}
                        >
                          Email
                        </SizableText>
                        <SizableText
                          size="$4"
                          color={theme ? "#1e293b" : "#f1f5f9"}
                          fontWeight="600"
                        >
                          {data.email}
                        </SizableText>
                      </YStack>
                    </XStack>
                  )}
                </YStack>

                {/* Action Button */}
                <Button
                  backgroundColor="#1877F2"
                  color="white"
                  marginTop="$4"
                  onPress={async () => {
                    const url = `https://facebook.com/${data.id}`;
                    try {
                      await Linking.openURL(url);
                    } catch (error) {
                      console.error("Error opening Facebook profile:", error);
                    }
                  }}
                  borderRadius={12}
                  paddingVertical="$3"
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialIcons name="open-in-new" size={18} color="white" />
                    <SizableText color="white" fontWeight="600" size="$4">
                      View Profile on Facebook
                    </SizableText>
                  </XStack>
                </Button>
              </Card>
            </Animatable.View>

            {/* Success Message */}
            <Animatable.View animation="fadeInUp" duration={600} delay={200}>
              <Card
                backgroundColor={theme ? "#f0fdf4" : "#064e3b"}
                borderColor={theme ? "#dcfce7" : "#065f46"}
                borderRadius={16}
                padding="$5"
                elevate
              >
                <YStack alignItems="center" gap="$3">
                  <View style={{
                    backgroundColor: theme ? "#d1fae5" : "#065f46",
                    borderRadius: 50,
                    padding: 12
                  }}>
                    <MaterialIcons name="check-circle" size={32} color={theme ? "#10b981" : "#34d399"} />
                  </View>
                  <H4
                    color={theme ? "#059669" : "#10b981"}
                    fontWeight="600"
                    textAlign="center"
                  >
                    Facebook Connected Successfully!
                  </H4>
                  <SizableText
                    size="$4"
                    color={theme ? "#065f46" : "#34d399"}
                    textAlign="center"
                  >
                    Your Facebook account is connected. Additional features like posts, pages, and analytics require extended permissions.
                  </SizableText>
                </YStack>
              </Card>
            </Animatable.View>
          </>
        )}
      </YStack>
    </ErrorBoundary>
  );
};

// Remove the Stat component since we're not using statistics anymore
export default FacebookOptions;
